// Chakra imports
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  Divider,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import CustomButton from 'components/CustomButton';
import {
  bindPagbankAccount,
  createPagbankSubaccount,
  getPagbankAccount,
  getPagbankConnectUrl,
  PagbankAccountStatus,
  revalidatePagbankAccount,
} from './api';
import SubaccountForm from './components/SubaccountForm';

export default function PagbankAccount() {
  const toast = useToast();
  const [data, setData] = useState<PagbankAccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'idle' | 'create' | 'bind'>('idle');
  const [accoId, setAccoId] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await getPagbankAccount());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /**
   * Só mensagem de erro de domínio chega ao lojista.
   *
   * O backend carimba `code` nos erros que ele mesmo escreveu — e esses são em
   * português e falam da operação. Erro sem `code` é interno (texto em inglês,
   * id de integração, stack do Prisma) e não ajuda quem está na tela: vira o
   * genérico, e o detalhe fica no log do servidor.
   */
  const fail = (err: any) => {
    const data = err?.response?.data;
    toast({
      status: 'error',
      title: 'Não foi possível concluir',
      description:
        data?.code && typeof data?.message === 'string'
          ? data.message
          : 'Tente novamente em instantes. Se continuar, fale com o suporte.',
    });
  };

  const bind = async () => {
    setSaving(true);
    try {
      await bindPagbankAccount(accoId.trim());
      toast({ status: 'success', title: 'Conta vinculada' });
      setMode('idle');
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const create = async (payload: any) => {
    setSaving(true);
    try {
      await createPagbankSubaccount(payload);
      toast({ status: 'success', title: 'Conta criada' });
      setMode('idle');
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  // Connect: o lojista autoriza no site da PagSeguro e volta pelo callback.
  const conectar = async () => {
    setSaving(true);
    try {
      const { url } = await getPagbankConnectUrl(window.location.origin);
      window.location.href = url;
    } catch (err) {
      fail(err);
      setSaving(false);
    }
  };

  const revalidate = async () => {
    setSaving(true);
    try {
      await revalidatePagbankAccount();
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box pt={{ base: '80px' }}>Carregando...</Box>;

  // Empresa sem a feature liberada não vê nada disso.
  if (!data?.enabled) {
    return (
      <Box pt={{ base: '80px' }}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          A conta PagSeguro não está habilitada para o seu estabelecimento. Fale com o seu gerente.
        </Alert>
      </Box>
    );
  }

  const account = data.account;

  return (
    <Box pt={{ base: '80px', md: '80px', xl: '80px' }}>
      <SimpleGrid columns={{ sm: 1 }} spacing="20px">
        <Card>
          <CardBody>
            <Heading size="md" mb={2}>
              Minha conta PagSeguro
            </Heading>
            <Text color="gray.500" mb={4}>
              É por essa conta que você recebe a sua parte de cada venda, direto da PagSeguro.
            </Text>

            {!account && !data.canCreate && (
              <Text>Você ainda não tem uma conta vinculada.</Text>
            )}

            {!account && data.canCreate && (
              <Stack spacing={4}>
                <Text>Você ainda não tem uma conta vinculada.</Text>
                <HStack>
                  <CustomButton onClick={() => setMode('create')}>
                    Criar minha conta na PagSeguro
                  </CustomButton>
                  <Button variant="outline" onClick={conectar} isLoading={saving}>
                    Já tenho conta PagSeguro
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setMode('bind')}>
                    Informar ID manualmente
                  </Button>
                </HStack>
              </Stack>
            )}

            {account && (
              <Stack spacing={3}>
                <HStack>
                  <Text fontWeight="bold">Conta:</Text>
                  <Code>{account.accoId}</Code>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">Recebimento por split:</Text>
                  {account.canReceiveSplit ? (
                    <Badge colorScheme="green">Ativo</Badge>
                  ) : (
                    <Badge colorScheme="orange">Pendente</Badge>
                  )}
                </HStack>

                {account.pendingKyc && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold">Falta concluir seu cadastro no app da PagSeguro.</Text>
                      <Text fontSize="sm">
                        Você já recebe a sua parte das vendas nessa conta, mas só consegue sacar
                        depois de enviar documento e selfie no aplicativo da PagSeguro.
                      </Text>
                    </Box>
                  </Alert>
                )}

                <Box>
                  <Button size="sm" variant="outline" onClick={revalidate} isLoading={saving}>
                    Atualizar situação
                  </Button>
                </Box>
              </Stack>
            )}

            {!account && (mode === 'bind' || !data.canCreate) && (
              <>
                <Divider my={4} />
                <Stack spacing={3}>
                  <Text fontWeight="bold">Informe o ID da sua conta PagSeguro</Text>
                  <Input
                    placeholder="ACCO_00000000-0000-0000-0000-000000000000"
                    value={accoId}
                    onChange={(e) => setAccoId(e.target.value)}
                  />
                  <HStack>
                    <CustomButton onClick={bind} isLoading={saving}>
                      Vincular
                    </CustomButton>
                    {data.canCreate && (
                      <Button variant="ghost" onClick={() => setMode('idle')}>
                        Cancelar
                      </Button>
                    )}
                  </HStack>
                </Stack>
              </>
            )}

            {mode === 'create' && data.canCreate && (
              <>
                <Divider my={4} />
                <SubaccountForm
                  saving={saving}
                  onCancel={() => setMode('idle')}
                  onSubmit={create}
                />
              </>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
